import PrivateImage from '../presentation/PrivateImage';
import React, {useEffect, useRef, useState} from "react";
import {Redirect, useParams} from "react-router-dom";
import {Button, Divider, Grid, Toolbar} from "@material-ui/core";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import {makeStyles} from "@material-ui/core/styles";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import {PDFExport} from "@progress/kendo-react-pdf";
import {NotificationManager} from "react-notifications";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {getPdfExportProxyProps, savePdfWithEmbeddedImages} from "../../helpers/pdfExport";
import ActionButton from "../form/input/ActionButton";
import GeneratedDocumentEmailAction from "./GeneratedDocumentEmailAction";
import "../../Print.css";

const useStyles = makeStyles((theme) => ({
    root: {flexGrow: 1, width: "100%", backgroundColor: theme.palette.background.paper},
    seeMore: {marginTop: theme.spacing(8)},
    paper: {padding: theme.spacing(2), display: "flex", overflow: "auto", flexDirection: "column"},
    marginTop: {marginTop: theme.spacing(2)},
    editHint: {margin: "0 0 12px", fontWeight: 700, textAlign: "center", color: "#2c4767"},
    document: {
        position: "relative",
        boxSizing: "border-box",
        width: "210mm",
        height: "297mm",
        padding: "16mm 17mm 14mm",
        overflow: "hidden",
        background: "#fffefb",
        color: "#1f2b3a",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 12,
        lineHeight: 1.55,
    },
    watermark: {position: "absolute", width: "120mm", maxHeight: "120mm", left: "45mm", top: "95mm", objectFit: "contain", opacity: .045},
    content: {position: "relative", zIndex: 1},
    header: {textAlign: "center", paddingBottom: "5mm", borderBottom: "2px solid #b08d57", marginBottom: "8mm"},
    eyebrow: {fontSize: 10, fontWeight: 700, letterSpacing: ".2em", textTransform: "uppercase", color: "#b08d57"},
    title: {margin: "2mm 0 0", fontSize: 23, lineHeight: 1.2, color: "#1f2b3a"},
    meta: {marginTop: "2mm", fontSize: 9, color: "#687586"},
    panel: {padding: "4mm 5mm", border: "1px solid #d7c7ab", borderRadius: 5, background: "rgba(255,254,251,.82)", marginBottom: "6mm"},
    sectionTitle: {margin: "0 0 3mm", paddingBottom: "1mm", borderBottom: "1px solid #decfb5", color: "#b08d57", fontSize: 10, fontWeight: 700, letterSpacing: ".13em", textTransform: "uppercase"},
    row: {display: "flex", alignItems: "baseline", minHeight: "7mm", gap: 4},
    label: {whiteSpace: "nowrap", color: "#5f6d7d", fontSize: 10, fontWeight: 600},
    input: {flex: 1, minWidth: 0, padding: "0 2px 1px", overflow: "hidden", border: 0, borderBottom: "1px dotted #8b96a3", background: "transparent", color: "#1f2b3a", fontFamily: '"DejaVu Sans", "Arial", sans-serif', fontSize: 11, fontWeight: 700},
    fullInput: {boxSizing: "border-box", width: "100%"},
    recipientHint: {marginTop: 1, color: "#768292", fontSize: 8.5},
    subject: {margin: "6mm 0 4mm", fontWeight: 700, color: "#1f2b3a"},
    paragraph: {margin: "0 0 4mm", textAlign: "justify"},
    inlineInput: {display: "inline-block", width: "55mm", margin: "0 2px"},
    delivery: {marginTop: "5mm"},
    deliveryInput: {boxSizing: "border-box", width: "100%", marginTop: 2},
    closing: {marginTop: "8mm"},
    signature: {width: "70mm", marginTop: "15mm", paddingTop: "2mm", borderTop: "1px dotted #8b96a3", textAlign: "center", color: "#5f6d7d", fontSize: 10},
}));

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("sk-SK", {day: "2-digit", month: "2-digit", year: "numeric"});
};

const initialFields = {
    applicant: "",
    applicantBirth: "",
    applicantAddress: "",
    applicantContact: "",
    recipient: "",
    location: "",
    requestDate: formatDate(new Date()),
    deceased: "",
    deceasedBirth: "",
    deceasedDeath: "",
    relationship: "",
    delivery: "osobným prevzatím / zaslaním poštou / elektronicky",
};

const AutopsyRequestContext = React.createContext(null);

const Input = ({name, className = ""}) => {
    const {classes, fields, handleChange} = React.useContext(AutopsyRequestContext);
    return <input name={name} value={fields[name]} onChange={handleChange} className={`${classes.input} ${className}`}/>;
};

const AutopsyReportRequestGenerator = (props) => {
    const classes = useStyles();
    const pdfRef = useRef(null);
    const funeralId = useParams();
    const userLocal = JSON.parse(localStorage.getItem("user")).id;
    const [funeral, setFuneral] = useState({_id: "", deceased: "", ca_email: ""});
    const [user, setUser] = useState(null);
    const [fields, setFields] = useState(initialFields);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const handleLoadError = (error) => {
            if (error.response?.status === 401) {
                logOut(props);
                NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
            }
        };

        UserService.getUser(userLocal).then((response) => {
            setUser(response.data);
            setFields((current) => ({...current, location: response.data.defined_texts?.default_signature_place || ""}));
        }, handleLoadError);

        UserService.getFuneral(funeralId.funeral_id).then((response) => {
            const currentFuneral = response.data;
            setFuneral(currentFuneral);
            setFields((current) => ({...current,
                applicant: currentFuneral.contracting_authority || "",
                applicantBirth: formatDate(currentFuneral.ca_birth),
                applicantAddress: [currentFuneral.ca_address, currentFuneral.ca_zip, currentFuneral.ca_city].filter(Boolean).join(" "),
                applicantContact: [currentFuneral.ca_phone, currentFuneral.ca_email].filter(Boolean).join(" / "),
                deceased: currentFuneral.deceased || "",
                deceasedBirth: formatDate(currentFuneral.d_birth),
                deceasedDeath: formatDate(currentFuneral.d_death),
                relationship: currentFuneral.ca_relationship || "",
            }));
        }, handleLoadError);
    }, [funeralId.funeral_id, userLocal, props]);

    const handleChange = ({target: {name, value}}) => setFields((current) => ({...current, [name]: value}));
    const handleExport = () => savePdfWithEmbeddedImages(pdfRef, (error) => {
        if (error) NotificationManager.error("Nepodarilo sa exportovať PDF!", "Chyba", 5000);
    });
    const fileSuffix = (funeral.deceased || "zosnuly").replace(/\s/g, "");

    return (
        <Container maxWidth="lg" className={classes.seeMore}>
            <div className={classes.root}>
                <Paper className={classes.paper}>
                    <Grid container>
                        <Grid xs={12} sm={4} md={3} item container><Toolbar><Button variant="contained" color="secondary" onClick={() => setSuccess(true)}>Späť na detaily</Button></Toolbar></Grid>
                        <Grid xs={12} sm={8} md={9} item container justifyContent="flex-end">
                            <Toolbar className="document-actions-toolbar">
                                <ActionButton color="primary"><Tooltip title="Pdf export"><PictureAsPdfIcon fontSize="small" onClick={handleExport}/></Tooltip></ActionButton>
                                <GeneratedDocumentEmailAction pdfRef={pdfRef} funeral={funeral} user={user}/>
                            </Toolbar>
                        </Grid>
                    </Grid>
                    <Divider/>
                    <Grid className={classes.marginTop} container direction="column" alignItems="center">
                        <div className={classes.editHint}>Po KLIKNUTÍ viete dopisovať alebo meniť údaje.</div>
                        <div className="document-preview-frame">
                            <PDFExport ref={pdfRef} {...getPdfExportProxyProps()} paperSize="A4" scale={0.75} fileName={`ziadost-o-vydanie-podrobnej-pitevnej-spravy-${fileSuffix}`}>
                                <AutopsyRequestContext.Provider value={{classes, fields, handleChange}}>
                                <div className={`a4-paper ${classes.document}`}>
                                    {user?.logo && <PrivateImage src={user.logo} className={classes.watermark} alt=""/>}
                                    <div className={classes.content}>
                                        <header className={classes.header}>
                                            <div className={classes.eyebrow}>{user?.name || "Pohrebná služba"}</div>
                                            <h1 className={classes.title}>Žiadosť o vydanie podrobnej pitevnej správy</h1>
                                            {user && <div className={classes.meta}>{[user.address, user.ico && `IČO: ${user.ico}`, user.phone, user.email_official].filter(Boolean).join(" · ")}</div>}
                                        </header>

                                        <section className={classes.panel}>
                                            <h2 className={classes.sectionTitle}>Žiadateľ</h2>
                                            <label className={classes.row}><span className={classes.label}>Meno a priezvisko:</span><Input name="applicant"/></label>
                                            <label className={classes.row}><span className={classes.label}>Dátum narodenia:</span><Input name="applicantBirth"/></label>
                                            <label className={classes.row}><span className={classes.label}>Adresa trvalého pobytu:</span><Input name="applicantAddress"/></label>
                                            <label className={classes.row}><span className={classes.label}>Telefón / e-mail:</span><Input name="applicantContact"/></label>
                                        </section>

                                        <section className={classes.panel}>
                                            <h2 className={classes.sectionTitle}>Adresát</h2>
                                            <Input name="recipient" className={classes.fullInput}/>
                                            <div className={classes.recipientHint}>(názov zdravotníckeho zariadenia, súdneho lekárstva alebo príslušného pracoviska)</div>
                                        </section>

                                        <div className={classes.row}>
                                            <span className={classes.label}>V</span><Input name="location"/>
                                            <span className={classes.label}>, dňa</span><Input name="requestDate"/>
                                        </div>

                                        <div className={classes.subject}>Vec: Žiadosť o vydanie podrobnej pitevnej správy</div>
                                        <p className={classes.paragraph}>Týmto Vás žiadam o vydanie úplnej podrobnej pitevnej správy vrátane záverov pitvy, určenia príčiny smrti a ďalších súvisiacich odborných nálezov týkajúcich sa zosnulého/zosnulej.</p>

                                        <section className={classes.panel}>
                                            <h2 className={classes.sectionTitle}>Zosnulý / zosnulá</h2>
                                            <label className={classes.row}><span className={classes.label}>Meno a priezvisko:</span><Input name="deceased"/></label>
                                            <label className={classes.row}><span className={classes.label}>Dátum narodenia:</span><Input name="deceasedBirth"/></label>
                                            <label className={classes.row}><span className={classes.label}>Dátum úmrtia:</span><Input name="deceasedDeath"/></label>
                                        </section>

                                        <p className={classes.paragraph}>Som (uveďte vzťah k zosnulému): <Input name="relationship" className={classes.inlineInput}/> a žiadam o sprístupnenie uvedenej dokumentácie v súlade s platnými právnymi predpismi.</p>
                                        <div className={classes.delivery}>
                                            <div className={classes.label}>Žiadam o vydanie dokumentácie:</div>
                                            <Input name="delivery" className={classes.deliveryInput}/>
                                            <div className={classes.recipientHint}>(vyberte alebo upravte požadovanú možnosť)</div>
                                        </div>

                                        <p className={classes.closing}>Za vybavenie mojej žiadosti Vám vopred ďakujem.</p>
                                        <p>S úctou</p>
                                        <div className={classes.signature}>podpis žiadateľa</div>
                                    </div>
                                </div>
                                </AutopsyRequestContext.Provider>
                            </PDFExport>
                        </div>
                    </Grid>
                </Paper>
            </div>
            {success ? <Redirect to={{pathname: `/pohreb/${funeralId.funeral_id}/info`, state: {from: props.location, number: 5}}}/> : <></>}
        </Container>
    );
};

export default AutopsyReportRequestGenerator;
